import { CaledarFilters, ICalendarPropState } from '@app/react/types/calendar';
import { atom, AtomEffect, RecoilState, selector } from 'recoil';
import {
	CalendarFiltersOptionsDto,
	CalendarResourceDto,
	FilterDto,
	ResourceTypeEnum,
	SimpleFacilityDto,
} from '@bondsports/types';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { DateTimeFormats, formatDate, TimeUnit } from '@bondsports/date-time';
import { isMobilePhone } from 'ui';
import { getDefaultCalendarViewPrefs, ICalendarViewPrefs } from '@app/react/lib/calendarViewPrefs';

dayjs.extend(isoWeek);

const slots = atom<{ events: any; eventsToSpaces: any }>({
	key: 'calendar_slots',
	default: { events: {}, eventsToSpaces: {} },
});
const draftSlots = atom<{ events: any; eventsToSpaces: any }>({
	key: 'calendar_draftSlots',
	default: { events: {}, eventsToSpaces: {} },
});

const selectedFacilityFullObject = atom<SimpleFacilityDto | null>({
	key: 'calendar_selectedFacilityFullObject',
	default: null,
});

const selectedFacility = selector<number>({
	key: 'calendar_selectedFacility',
	get: ({ get }) => {
		const selectedFacility: SimpleFacilityDto | null = get(selectedFacilityFullObject);
		return selectedFacility?.id || 0;
	},
});

const today = formatDate(new Date(), DateTimeFormats.YYYY_MM_DD, false);

const selectedDate = atom<string>({
	key: 'calendar_selectedDate',
	default: today,
});

const resources = atom<CalendarResourceDto[]>({
	key: 'calendar_resources',
	default: [],
});

const showSpaces = atom<{ [key: number]: number[] }>({
	key: 'calendar_showSpaces',
	default: {},
});

export enum ECalendarMode {
	DAILY = 'daily',
	WEEKLY = 'weekly',
	MONTHLY = 'monthly',
	DAYX = 'dayx',
}

export enum ECalendarView {
	VERTICAL = 'vertical',
	HORIZONTAL = 'horizontal',
	LIST = 'list',
}

const view = atom<ECalendarView>({
	key: 'calendar_view',
	default: ECalendarView.VERTICAL,
});

const mode = atom<ECalendarMode>({
	key: 'calendar_mode',
	default: ECalendarMode.DAILY,
});

export enum MobileCalendarViewEnum {
	DAY = 'day',
	TIMELINE = 'timeline',
	AGENDA = 'agenda',
}

export const DEFAULT_DAY_RANGE = 7;
export const MOBILE_DAY_RANGE = 1;

/** Phone column count per saved view id, kept on the device until the API persists `options.mobileColumns`. */
export interface IMobileColumnsByFilter {
	[filterId: number]: number;
}

const MOBILE_COLUMNS_STORAGE_KEY = 'calendar-mobile-columns';

const mobileColumnsStorageEffect: AtomEffect<IMobileColumnsByFilter> = ({ setSelf, onSet }) => {
	try {
		const saved: string | null = localStorage.getItem(MOBILE_COLUMNS_STORAGE_KEY);

		if (saved) {
			setSelf(JSON.parse(saved));
		}
	} catch (error) {
		console.warn('Failed to restore calendar mobile columns from localStorage:', error);
	}

	onSet((newValue, _, isReset) => {
		try {
			if (isReset) {
				localStorage.removeItem(MOBILE_COLUMNS_STORAGE_KEY);
			} else {
				localStorage.setItem(MOBILE_COLUMNS_STORAGE_KEY, JSON.stringify(newValue));
			}
		} catch (error) {
			console.warn('Failed to save calendar mobile columns to localStorage:', error);
		}
	});
};

const mobileColumnsByFilter = atom<IMobileColumnsByFilter>({
	key: 'calendar_mobileColumnsByFilter',
	default: {},
	effects: [mobileColumnsStorageEffect],
});

const dateRange = atom<{ startDate: string; endDate: string }>({
	key: 'calendar_dateRange',
	default: { startDate: today, endDate: today },
});

const isFilters = atom<boolean>({
	key: 'calendar_isFilters',
	default: false,
});

const filters = atom<CaledarFilters | null>({
	key: 'calendar_filters',
	default: null,
});

export interface IGroup {
	name: string;
	childrenSlotIds: number[];
}

export interface IGroups {
	[x: number]: IGroup[];
}

const groups = atom<IGroups>({
	key: 'calendar_groups',
	default: {},
});

interface IFilterResources {
	[x: number]: number[];
}

const filterResources = atom<IFilterResources | {}>({
	key: 'calendar_filterResources',
	default: {},
});

const selectedMonthlyResource = atom<number>({
	key: 'calendar_selectedMonthlyResource',
	default: 0,
});

const isLoading = atom<boolean>({
	key: 'calendar_isLoading',
	default: false,
});

const isFirstRender = atom<boolean>({
	key: 'calendar_isFirstRender',
	default: true,
});

const groupedResources = atom<number[]>({
	key: 'calendar_groupedResources',
	default: [],
});

const scrollTo = atom<any>({
	key: 'calendar_scrollTo',
	default: null,
});

const calendarPropState = atom<ICalendarPropState>({
	key: 'calendar_calendarPropState',
	default: undefined,
});

const calendarKey = atom<number>({
	key: 'calendar_calendarKey',
	default: 0,
});

const selectedGroup = atom<{ [x: number]: string }>({
	key: 'calendar_selectedGroup',
	default: {},
});

const scrollPosition = atom<{ scrollTop: number; scrollLeft: number }>({
	key: 'calendar_scrollPosition',
	default: {
		scrollTop: 0,
		scrollLeft: 0,
	},
});

const filtersOptions = atom<CalendarFiltersOptionsDto | null>({
	key: 'calendar_filtersOptions',
	default: null,
});

const savedViews = atom<{ resourceTypes: ResourceTypeEnum[] }>({
	key: 'calendar_savedViews',
	default: { resourceTypes: [ResourceTypeEnum.SPACE] },
});

const loadingFilters = atom<boolean>({
	key: 'calendar_loadingFilters',
	default: false,
});

const errorFetchingFilters = atom<string>({
	key: 'calendar_errorFetchingFilters',
	default: '',
});

const weeklyRange = selector({
	key: 'calendar_weeklyRange',
	get: ({ get }) => {
		const calendarMode: ECalendarMode = get(mode);
		const currentDate: string = get(selectedDate);

		if (calendarMode === ECalendarMode.DAYX) {
			const days: number = get(dayRange);
			return {
				rangeStart: dayjs.utc(currentDate).format(DateTimeFormats.YYYY_MM_DD),
				rangeEnd: dayjs
					.utc(currentDate)
					.add(days || 1, TimeUnit.DAY)
					.format(DateTimeFormats.YYYY_MM_DD),
			};
		}

		const start: string = dayjs.utc(currentDate).isoWeekday(1).format(DateTimeFormats.YYYY_MM_DD);
		const end: string = dayjs.utc(currentDate).isoWeekday(7).format(DateTimeFormats.YYYY_MM_DD);

		return {
			rangeStart: start,
			rangeEnd: end,
		};
	},
});

const filter: RecoilState<FilterDto> = atom<FilterDto>({
	key: 'calendar_filter',
	default: null,
});

const dayRange = atom<number>({
	key: 'calendar_dayRange',
	default: isMobilePhone() ? MOBILE_DAY_RANGE : DEFAULT_DAY_RANGE,
});

const viewPrefs = atom<ICalendarViewPrefs>({
	key: 'calendar_viewPrefs',
	default: getDefaultCalendarViewPrefs(),
});

export const calendarStore = {
	isFirstRender,
	mobileColumnsByFilter,
	slots,
	scrollTo,
	selectedFacility,
	resources,
	selectedDate,
	mode,
	isFilters,
	filters,
	groups,
	showSpaces,
	filterResources,
	view,
	selectedMonthlyResource,
	dateRange,
	draftSlots,
	isLoading,
	groupedResources,
	calendarPropState,
	calendarKey,
	selectedGroup,
	scrollPosition,
	selectedFacilityFullObject,
	filtersOptions,
	savedViews,
	errorFetchingFilters,
	loadingFilters,
	weeklyRange,
	filter,
	dayRange,
	viewPrefs,
};
